export function createTranscriptView({
  document,
  window,
  dummyFiles,
  transcriptCache,
  fallbackTranscript,
  demoRecorderSecondSegment,
  getTranscriptBaseName,
  getDisplayName,
  getTranscriptCacheKey,
  loadTranscriptContent,
  formatFullDate,
  updateDemoButtons,
  applyPendingInDocSearchPreset = () => {},
  setFilter = () => {},
  showLibrary = () => {},
  showVendingMachine = () => {},
  renderFileList = () => {},
  refreshFileRow = () => {},
  forceCompleteFile = () => {},
  cancelFile = () => {},
}) {
  const vendingMachine = document.getElementById("vendingMachine");
  const libraryView = document.getElementById("libraryView");
  const transcriptElements = {
    get container() {
      return document.getElementById("transcriptView");
    },
    get title() {
      return document.getElementById("transcriptTitle");
    },
    get meta() {
      return document.getElementById("transcriptMeta");
    },
    get bodyWrapper() {
      return document.getElementById("transcriptBody");
    },
    get body() {
      const wrapper = document.getElementById("transcriptBody");
      return wrapper ? wrapper.querySelector("[data-scroll-viewport]") : null;
    },
    get content() {
      return document.getElementById("transcriptContent");
    },
    get pending() {
      return document.getElementById("transcriptPending");
    },
    get defaultActions() {
      return document.getElementById("transcriptDefaultActions");
    },
    get draftActions() {
      return document.getElementById("transcriptDraftActions");
    },
    get saveButton() {
      return document.getElementById("transcriptSaveBtn");
    },
    get discardButton() {
      return document.getElementById("transcriptDiscardBtn");
    },
    get recorderContainer() {
      return document.getElementById("transcriptRecorderContainer");
    },
    get copyButton() {
      return document.querySelector(
        "#transcriptDefaultActions .transcript-view__action--primary",
      );
    },
    get downloadButton() {
      return document.querySelector(
        "#transcriptDefaultActions button[onclick='downloadCurrentTranscript()']",
      );
    },
  };

  let transcriptSearchHotkeys = null;
  let transcriptScrollbar = null;
  let transcriptPendingTimer = null;
  // [MICROPHONE MODE - UNRELEASED] ellipsis animation for live recording transcript preview
  let transcriptEllipsisTimer = null;
  let transcriptEllipsisNode = null;
  let transcriptReadyResolve = null;
  let transcriptReadyPromise = Promise.resolve();
  let currentTranscriptFilename = "";
  let currentTranscriptContent = "";
  let currentTranscriptIsDraft = false;
  let currentTranscriptIsRecorded = false;
  let updateTranscriptStats = () => {};

  function setStatsHandlers({ updateTranscriptStats: updateStats }) {
    if (typeof updateStats === "function") {
      updateTranscriptStats = updateStats;
    }
  }

  function getTranscriptElements() {
    return transcriptElements;
  }

  function getTranscriptScrollbar() {
    return ensureTranscriptScrollbar();
  }

  function setTranscriptSearchHotkeys(hotkeys) {
    transcriptSearchHotkeys = hotkeys || null;
  }

  function getTranscriptSearchHotkeys() {
    return transcriptSearchHotkeys;
  }

  function getCurrentTranscriptFilename() {
    return currentTranscriptFilename;
  }

  function setCurrentTranscriptFilename(value) {
    currentTranscriptFilename = value || "";
  }

  function getCurrentTranscriptContent() {
    return currentTranscriptContent;
  }

  function setCurrentTranscriptContent(value) {
    currentTranscriptContent = typeof value === "string" ? value : "";
  }

  function getCurrentTranscriptIsDraft() {
    return currentTranscriptIsDraft;
  }

  function setTranscriptRecorded(isRecorded) {
    currentTranscriptIsRecorded = !!isRecorded;
    applyTranscriptActionLabels();
  }

  function ensureTranscriptScrollbar() {
    if (
      !transcriptScrollbar &&
      window.CustomScrollbars &&
      transcriptElements.bodyWrapper &&
      transcriptElements.body
    ) {
      transcriptScrollbar = window.CustomScrollbars.enhance(
        transcriptElements.bodyWrapper,
        {
          viewport: transcriptElements.body,
        },
      );
    }
    return transcriptScrollbar;
  }

  // [MICROPHONE MODE - UNRELEASED] animated ellipsis appended to live transcript content
  // shows "..." cycling animation while recording is being transcribed in real-time
  function startTranscriptEllipsis() {
    if (!transcriptElements || !transcriptElements.content) return;
    const contentEl = transcriptElements.content;
    if (transcriptEllipsisTimer) return;
    if (!contentEl) return;
    try {
      const spacer = document.createTextNode(" ");
      const span = document.createElement("span");
      span.className = "transcript-ellipsis";
      span.setAttribute("aria-hidden", "true");
      span.style.whiteSpace = "pre";
      span.style.marginLeft = "2px";
      span.style.display = "inline-block";
      span.style.transformOrigin = "bottom left";
      span.textContent = "";
      contentEl.appendChild(spacer);
      contentEl.appendChild(span);
      transcriptEllipsisNode = span;
      let dots = 0;
      transcriptEllipsisTimer = setInterval(() => {
        dots = (dots + 1) % 4;
        if (transcriptEllipsisNode) {
          transcriptEllipsisNode.textContent = ".".repeat(dots);
          const scale =
            dots === 0 ? 1 : dots === 1 ? 1.04 : dots === 2 ? 1.07 : 1.02;
          transcriptEllipsisNode.style.transform = `translateY(${dots === 2 ? -1 : 0}px) scale(${scale})`;
        }
      }, 400);
    } catch (_) {}
  }

  // [MICROPHONE MODE - UNRELEASED] cleanup for ellipsis animation
  function stopTranscriptEllipsis() {
    try {
      if (transcriptEllipsisTimer) {
        clearInterval(transcriptEllipsisTimer);
        transcriptEllipsisTimer = null;
      }
      if (transcriptEllipsisNode && transcriptEllipsisNode.parentNode) {
        transcriptEllipsisNode.parentNode.removeChild(transcriptEllipsisNode);
      }
      transcriptEllipsisNode = null;
    } catch (_) {}
  }

  function cancelTranscriptPendingTimer() {
    if (transcriptPendingTimer) {
      clearTimeout(transcriptPendingTimer);
      transcriptPendingTimer = null;
    }
  }

  function setTranscriptState(state) {
    const container = transcriptElements.container;
    if (!container) return;
    if (state) {
      container.setAttribute("data-transcript-state", state);
    } else {
      container.removeAttribute("data-transcript-state");
    }
    updateTranscriptActionAvailability();
  }

  function updateTranscriptActionAvailability() {
    const container = transcriptElements.container;
    const isPending =
      container &&
      (container.classList.contains("is-pending") ||
        container.classList.contains("is-pending-lite"));
    const disableDraftActions = !currentTranscriptIsDraft || !!isPending;
    const saveBtn = transcriptElements.saveButton;
    if (saveBtn) {
      saveBtn.disabled = disableDraftActions;
    }
    const discardBtn = transcriptElements.discardButton;
    if (discardBtn) {
      discardBtn.disabled = disableDraftActions;
    }
  }

  function setTranscriptDraftState(isDraft) {
    currentTranscriptIsDraft = !!isDraft;
    const container = transcriptElements.container;
    if (container) {
      container.classList.toggle("is-draft", currentTranscriptIsDraft);
    }
    const defaultActions = transcriptElements.defaultActions;
    if (defaultActions) {
      defaultActions.setAttribute(
        "aria-hidden",
        currentTranscriptIsDraft ? "true" : "false",
      );
    }
    const draftActions = transcriptElements.draftActions;
    if (draftActions) {
      draftActions.setAttribute(
        "aria-hidden",
        currentTranscriptIsDraft ? "false" : "true",
      );
    }
    updateTranscriptActionAvailability();
    applyTranscriptActionLabels();
  }

  function applyTranscriptActionLabels() {
    const recorderContainer = transcriptElements.recorderContainer;
    if (recorderContainer) {
      recorderContainer.style.display = currentTranscriptIsRecorded
        ? ""
        : "none";
    }
    const copyButton = transcriptElements.copyButton;
    const downloadButton = transcriptElements.downloadButton;
    const copyLabel = currentTranscriptIsRecorded
      ? "Copy Recording"
      : "Copy All";
    if (copyButton) {
      copyButton.dataset.defaultLabel = copyLabel;
      if (!copyButton.classList.contains("copied")) {
        copyButton.textContent = copyLabel;
      }
    }
    if (downloadButton) {
      downloadButton.textContent = "Download .txt";
      if (downloadButton.dataset && downloadButton.dataset.defaultLabel) {
        delete downloadButton.dataset.defaultLabel;
      }
    }
  }

  function describeRecordingDuration(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) return "";
    const rounded = Math.max(1, Math.round(seconds));
    if (rounded < 60) {
      return `${rounded}s capture`;
    }
    const minutes = Math.floor(rounded / 60);
    const rem = rounded % 60;
    const paddedSeconds = rem.toString().padStart(2, "0");
    return `${minutes}m ${paddedSeconds}s capture`;
  }

  function showTranscriptPendingUI(
    { filename, durationSeconds },
    options = {},
  ) {
    try {
      console.log("[demo-debug] pending-ui", {
        filename,
        durationSeconds,
        options,
      });
    } catch (_) {}
    cancelTranscriptPendingTimer();
    const treatAsDraft = options.treatAsDraft !== false;
    const preserveContent = options.preserveContent === true;
    setTranscriptDraftState(treatAsDraft);

    const displayName = filename ? getDisplayName(filename) : "Live Recording";
    const durationDescriptor = describeRecordingDuration(durationSeconds);

    setTranscriptState("pending");

    const container = transcriptElements.container;
    if (container) {
      container.classList.remove("is-pending");
      container.classList.remove("is-pending-lite");
    }
    if (transcriptElements.pending) {
      transcriptElements.pending.setAttribute("aria-hidden", "true");
      delete transcriptElements.pending.dataset.message;
    }
    if (transcriptElements.title) {
      transcriptElements.title.textContent = displayName;
    }
    if (transcriptElements.meta) {
      transcriptElements.meta.textContent = durationDescriptor
        ? `Transcribing | ${durationDescriptor}`
        : "Transcribing | calibrating levels";
    }
    if (transcriptElements.content && !preserveContent) {
      transcriptElements.content.textContent = "";
      try {
        transcriptElements.content.removeAttribute("data-transcript-ready");
        transcriptElements.content.removeAttribute("data-len");
      } catch (_) {}
    }

    if (!preserveContent) {
      updateTranscriptStats("");
    }
    updateTranscriptActionAvailability();
    showTranscriptView();
    setTranscriptDraftState(true);
    updateTranscriptActionAvailability();
    if (transcriptElements.saveButton)
      transcriptElements.saveButton.disabled = true;
    if (transcriptElements.discardButton)
      transcriptElements.discardButton.disabled = true;
    startTranscriptEllipsis();
  }

  function prepareTranscriptForContent() {
    if (transcriptElements.pending) {
      transcriptElements.pending.setAttribute("aria-hidden", "true");
      delete transcriptElements.pending.dataset.message;
      const messageEl = transcriptElements.pending.querySelector(
        "[data-pending-message]",
      );
      if (messageEl) messageEl.textContent = "";
    }
    const container = transcriptElements.container;
    if (container) {
      container.classList.remove("is-pending");
      container.classList.remove("is-pending-lite");
    }
    setTranscriptState("loading");
    updateTranscriptActionAvailability();
    stopTranscriptEllipsis();
  }

  function markTranscriptReady() {
    setTranscriptState("ready");
  }

  function startPendingTranscriptFlow(
    { filename, durationSeconds },
    options = {},
  ) {
    if (!filename) return;
    showTranscriptPendingUI({ filename, durationSeconds }, options);

    const delayMs = 1700 + Math.round(Math.random() * 200);
    transcriptPendingTimer = setTimeout(() => {
      transcriptPendingTimer = null;
      void viewTranscript(filename);
    }, delayMs);
  }

  function normalizeTranscriptContent(text) {
    if (typeof text !== "string") {
      return "";
    }
    return text.replace(/^[\s\uFEFF]+/, "");
  }

  function findTranscriptFile(name) {
    if (!name) return null;
    return dummyFiles.find((f) => f.name === name) || null;
  }

  async function viewTranscript(filename) {
    if (!filename) return;
    cancelTranscriptPendingTimer();
    try {
      transcriptReadyPromise = new Promise((resolve) => {
        transcriptReadyResolve = resolve;
      });
    } catch (_) {}
    setTranscriptDraftState(false);
    const content = await loadTranscriptContent(filename);
    prepareTranscriptForContent();
    const file = dummyFiles.find((f) => f.name === filename) || null;
    ensureTranscriptScrollbar();
    currentTranscriptIsRecorded = !!(file && file.isUserRecorded);

    if (file) {
      setTranscriptDraftState(!!file.isTemporary);
    } else {
      setTranscriptDraftState(false);
    }

    currentTranscriptFilename = filename;
    const normalizedContent = normalizeTranscriptContent(content);
    currentTranscriptContent = normalizedContent;

    if (transcriptElements.title) {
      transcriptElements.title.textContent = getDisplayName(filename);
    }

    if (transcriptElements.meta) {
      if (file) {
        const created = file.created ? formatFullDate(file.created) : "n/a";
        const duration = file.duration ? file.duration : "n/a";
        transcriptElements.meta.textContent = `Created ${created} | Duration ${duration}`;
      } else {
        transcriptElements.meta.textContent = "Demo transcript";
      }
    }

    if (transcriptElements.content) {
      transcriptElements.content.textContent = normalizedContent;
      try {
        transcriptElements.content.setAttribute("data-transcript-ready", "1");
        transcriptElements.content.setAttribute(
          "data-len",
          String((normalizedContent || "").length),
        );
      } catch (_) {}
      try {
        document.dispatchEvent(
          new CustomEvent("demo:transcript:opened", { detail: { filename } }),
        );
      } catch (_) {}
      try {
        if (typeof transcriptReadyResolve === "function") {
          transcriptReadyResolve();
        }
      } catch (_) {}
    } else if (
      window.__SIFT_DEMO_TEST_CONFIG &&
      window.__SIFT_DEMO_TEST_CONFIG.debug
    ) {
      try {
        console.log(
          "[demo debug] transcript content element missing during viewTranscript",
        );
      } catch (_) {}
    }

    if (transcriptElements.body) {
      transcriptElements.body.scrollTop = 0;
    }

    if (transcriptScrollbar) {
      transcriptScrollbar.refresh();
    }

    updateTranscriptStats(normalizedContent);
    showTranscriptView();
    if (typeof applyPendingInDocSearchPreset === "function") {
      applyPendingInDocSearchPreset(filename);
    }
    try {
      if (
        window.__SIFT_DEMO_TEST_CONFIG &&
        window.__SIFT_DEMO_TEST_CONFIG.debug
      ) {
        console.log("[demo debug] transcript loaded", {
          filename,
          length: normalizedContent ? normalizedContent.length : 0,
        });
      }
    } catch (_) {}
    try {
      window.dispatchEvent(
        new CustomEvent("demo:transcript:opened", { detail: { filename } }),
      );
    } catch (_) {}
    markTranscriptReady();
  }

  function showTranscriptView() {
    ensureTranscriptScrollbar();
    document.body.classList.add("showing-transcript");
    document.body.classList.remove("first-time-mode");
    setTranscriptDraftState(currentTranscriptIsDraft);
    if (transcriptElements.container) {
      transcriptElements.container.classList.add("active");
      transcriptElements.container.setAttribute("aria-hidden", "false");
      transcriptElements.container.style.display = "flex";
    }
    if (vendingMachine) vendingMachine.style.display = "none";
    if (libraryView) {
      libraryView.classList.remove("active");
      libraryView.style.display = "none";
    }
    if (transcriptScrollbar) {
      requestAnimationFrame(() => transcriptScrollbar.refresh());
    }
    // search bar is created on-demand when user presses / - don't auto-show
    if (typeof updateDemoButtons === "function") {
      updateDemoButtons("transcript");
    }
    if (window.DemoMode) {
      window.DemoMode.state.clearAll();
      window.DemoMode.state.set("transcript-view", true);
    }
  }

  function hideTranscriptView() {
    document.body.classList.remove("showing-transcript");
    cancelTranscriptPendingTimer();
    setTranscriptDraftState(false);
    currentTranscriptIsRecorded = false;
    applyTranscriptActionLabels();
    if (transcriptElements.pending) {
      transcriptElements.pending.setAttribute("aria-hidden", "true");
    }
    if (!transcriptElements.container) return;
    const activeElement = document.activeElement;
    if (activeElement && transcriptElements.container.contains(activeElement)) {
      activeElement.blur();
    }
    transcriptElements.container.classList.remove("active");
    transcriptElements.container.setAttribute("aria-hidden", "true");
    transcriptElements.container.style.display = "none";
    transcriptElements.container.classList.remove("is-pending");
    transcriptElements.container.classList.remove("is-pending-lite");
    setTranscriptState("");
    if (window.DemoMode) {
      window.DemoMode.state.set("transcript-view", false);
    }
  }

  function returnToLibrary() {
    if (typeof showLibrary === "function") showLibrary();
  }

  async function copyCurrentTranscript() {
    let textToCopy = currentTranscriptContent || "";
    if (!textToCopy && transcriptElements.content) {
      textToCopy = transcriptElements.content.textContent || "";
      currentTranscriptContent = textToCopy;
    }
    if (!textToCopy) {
      if (
        window.__SIFT_DEMO_TEST_CONFIG &&
        window.__SIFT_DEMO_TEST_CONFIG.debug
      ) {
        try {
          console.log(
            "[demo debug] copyCurrentTranscript aborted; content length 0",
          );
        } catch (_) {}
      }
      alert("Transcript not loaded yet.");
      return;
    }

    const copyButton = transcriptElements.copyButton;
    const defaultCopyLabel =
      (copyButton && copyButton.dataset && copyButton.dataset.defaultLabel) ||
      "Copy All";

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = textToCopy;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }

      if (copyButton) {
        copyButton.textContent = "Copied!";
        copyButton.classList.add("copied");
        setTimeout(() => {
          copyButton.textContent = defaultCopyLabel;
          copyButton.classList.remove("copied");
        }, 2000);
      }
    } catch (error) {
      console.warn("Unable to copy transcript", error);
      alert("Unable to copy transcript to clipboard.");
    }
  }

  function triggerTranscriptDownload(filename, content) {
    const link = document.createElement("a");
    const blob = new Blob([content], { type: "text/plain" });
    link.href = URL.createObjectURL(blob);
    link.download = `${getTranscriptBaseName(filename)}.txt`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
      link.remove();
    }, 0);
  }

  function downloadCurrentTranscript() {
    if (!currentTranscriptFilename) {
      alert("Open a transcript before downloading.");
      return;
    }

    triggerTranscriptDownload(
      currentTranscriptFilename,
      currentTranscriptContent || fallbackTranscript,
    );
  }

  function saveCurrentTranscript() {
    if (!currentTranscriptFilename) {
      returnToLibrary();
      return;
    }
    const file = findTranscriptFile(currentTranscriptFilename);
    if (!file) {
      setTranscriptDraftState(false);
      returnToLibrary();
      return;
    }
    file.isTemporary = false;
    if (file.status !== "complete") {
      forceCompleteFile(file, { suppressToast: true });
    } else {
      renderFileList();
    }
    setTranscriptDraftState(false);
    updateTranscriptStats(currentTranscriptContent || "");
    showTranscriptView();
  }

  function discardCurrentTranscript() {
    const file = currentTranscriptFilename
      ? findTranscriptFile(currentTranscriptFilename)
      : null;
    if (file) {
      cancelFile(file.id);
    }
    currentTranscriptFilename = "";
    currentTranscriptContent = "";
    setTranscriptDraftState(false);
    hideTranscriptView();
    const hasSavedFiles = dummyFiles.some(
      (f) => f.status === "complete" && !f.isTemporary,
    );
    if (hasSavedFiles) {
      returnToLibrary();
      setFilter("completed");
    } else {
      showVendingMachine();
    }
  }

  function substituteContinuationContent(file, combined) {
    const cacheKey = getTranscriptCacheKey(file.name);
    transcriptCache.set(cacheKey, combined);
    currentTranscriptContent = combined;
    const contentEl = transcriptElements.content;
    if (contentEl) {
      contentEl.textContent = combined;
      try {
        contentEl.setAttribute("data-transcript-ready", "1");
        contentEl.setAttribute("data-len", String((combined || "").length));
      } catch (_) {}
      updateTranscriptStats(combined);
      stopTranscriptEllipsis();
    }
  }

  async function finalizeTranscriptContinuation(
    file,
    durationSeconds,
    continuationContext,
  ) {
    if (!file) return;
    file.isUserRecorded = true;
    file.transcribedAt = new Date();
    file.status = "complete";
    file.progress = 100;
    file.isTemporary = true;
    currentTranscriptIsRecorded = true;
    applyTranscriptActionLabels();

    const cacheKey = getTranscriptCacheKey(file.name);
    let baseContent = continuationContext
      ? continuationContext.existingContent
      : "";
    if (!baseContent) {
      if (transcriptCache.has(cacheKey)) {
        baseContent = transcriptCache.get(cacheKey);
      } else {
        try {
          baseContent = await loadTranscriptContent(file.name);
        } catch (_) {
          baseContent = "";
        }
      }
    }

    const trimmedBase =
      typeof baseContent === "string" ? baseContent.trim() : "";
    const initialSegment =
      trimmedBase && trimmedBase.length > 0
        ? trimmedBase
        : file && typeof file.demoTranscriptContent === "string"
          ? file.demoTranscriptContent.trim()
          : "";
    const nextSegmentRaw =
      file && typeof file.demoTranscriptNextSegment === "string"
        ? file.demoTranscriptNextSegment
        : fallbackTranscript;
    const trimmedNext =
      typeof nextSegmentRaw === "string" ? nextSegmentRaw.trim() : "";

    if (file && (!trimmedNext || trimmedNext.length === 0)) {
      try {
        console.log(
          "[demo-debug] finalize-continuation:injecting-default-next-segment",
        );
      } catch (_) {}
      file.demoTranscriptNextSegment = demoRecorderSecondSegment;
    }

    const effectiveNext =
      file && typeof file.demoTranscriptNextSegment === "string"
        ? String(file.demoTranscriptNextSegment).trim()
        : trimmedNext;

    const segments = [];
    if (initialSegment && initialSegment.length > 0) {
      segments.push(initialSegment);
    }
    if (
      effectiveNext &&
      effectiveNext.length > 0 &&
      !segments.some((segment) => segment.includes(effectiveNext))
    ) {
      segments.push(effectiveNext);
    }
    if (segments.length === 0) {
      segments.push(fallbackTranscript);
    }

    const combined = segments.join(" ");
    try {
      console.log("[demo-debug] finalize-continuation:combined", {
        initialLen: (initialSegment || "").length,
        nextLen: (effectiveNext || "").length,
        combinedLen: (combined || "").length,
        includesSecond:
          (combined || "").indexOf(demoRecorderSecondSegment) !== -1,
      });
    } catch (_) {}

    if (file) {
      file.demoTranscriptContent = combined;
    }

    const isViewingTranscript =
      typeof document !== "undefined" &&
      document.body &&
      document.body.classList.contains("showing-transcript") &&
      currentTranscriptFilename === file.name;
    setTranscriptDraftState(false);
    try {
      console.log("[demo-debug] finalize-continuation:draft-cleared", {
        draft: currentTranscriptIsDraft,
        preserveContent: isViewingTranscript,
      });
    } catch (_) {}

    startPendingTranscriptFlow(
      { filename: file.name, durationSeconds },
      { treatAsDraft: false, preserveContent: isViewingTranscript },
    );
    try {
      console.log("[demo-debug] finalize-continuation:pending-queued", {
        filename: file.name,
        durationSeconds,
        preserveContent: isViewingTranscript,
      });
    } catch (_) {}

    if (isViewingTranscript && transcriptElements.content) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      substituteContinuationContent(file, combined);
    } else {
      substituteContinuationContent(file, combined);
    }

    currentTranscriptContent = combined;

    if (file) {
      refreshFileRow(file);
    } else {
      renderFileList();
    }
  }

  hideTranscriptView();

  return {
    getTranscriptElements,
    getTranscriptScrollbar,
    getTranscriptSearchHotkeys,
    setTranscriptSearchHotkeys,
    getCurrentTranscriptFilename,
    setCurrentTranscriptFilename,
    getCurrentTranscriptContent,
    setCurrentTranscriptContent,
    getCurrentTranscriptIsDraft,
    setTranscriptRecorded,
    showTranscriptPendingUI,
    prepareTranscriptForContent,
    markTranscriptReady,
    startPendingTranscriptFlow,
    showTranscriptView,
    hideTranscriptView,
    returnToLibrary,
    viewTranscript,
    copyCurrentTranscript,
    downloadCurrentTranscript,
    saveCurrentTranscript,
    discardCurrentTranscript,
    triggerTranscriptDownload,
    setTranscriptDraftState,
    applyTranscriptActionLabels,
    updateTranscriptActionAvailability,
    getTranscriptReadyPromise: () => transcriptReadyPromise,
    setStatsHandlers,
    finalizeTranscriptContinuation,
  };
}
